import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { ListenerApi } from './ListenerApi';
import { formatDuration, progressFromServerClock, reconnectDelayMs, selectInitialStation, sleepRemaining } from './playerModel';
import type { HistoryItem, PublicStation } from './types';
import { itemTypeLabel } from '../shared/i18n';
import './listener.css';

const api = new ListenerApi();
const rememberedStationKey = 'radio.listener.station';

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function rememberedStation(): string | null {
  try { return localStorage.getItem(rememberedStationKey); } catch { return null; }
}

export function ListenerPlayer(): ReactElement {
  const [stations, setStations] = useState<PublicStation[]>([]);
  const [selectedSlug, setSelectedSlug] = useState('');
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [realtime, setRealtime] = useState<'connecting' | 'live' | 'reconnecting'>('connecting');
  const [audioState, setAudioState] = useState<'idle' | 'loading' | 'playing' | 'paused' | 'reconnecting' | 'blocked'>('idle');
  const [receivedAt, setReceivedAt] = useState(Date.now());
  const [clock, setClock] = useState(Date.now());
  const [volume, setVolume] = useState(0.8);
  const [sleepDeadline, setSleepDeadline] = useState<number | null>(null);
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null);
  const [online, setOnline] = useState(navigator.onLine);
  const audioRef = useRef<HTMLAudioElement>(null);
  const userStarted = useRef(false);
  const reconnectAttempt = useRef(0);
  const reconnectTimer = useRef<number | null>(null);

  const selected = useMemo(() => stations.find(({ slug }) => slug === selectedSlug) ?? null, [stations, selectedSlug]);
  const nowPlaying = selected?.nowPlaying ?? null;
  const progress = progressFromServerClock(nowPlaying, receivedAt, clock);

  const loadHistory = useCallback(async (slug: string, signal?: AbortSignal): Promise<void> => {
    try { setHistory((await api.history(slug, signal)).items); } catch (reason) {
      if (!(reason instanceof DOMException && reason.name === 'AbortError')) setHistory([]);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void api.stations(controller.signal).then((page) => {
      setStations(page.items);
      const station = selectInitialStation(page.items, rememberedStation());
      if (station !== null) {
        setSelectedSlug(station.slug);
        setReceivedAt(Date.now());
      }
      setError(null);
    }).catch((reason: unknown) => {
      if (!(reason instanceof DOMException && reason.name === 'AbortError')) setError('Не вдалося завантажити станції. Перевірте з’єднання.');
    }).finally(() => setLoading(false));

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (selectedSlug === '') return;
    try { localStorage.setItem(rememberedStationKey, selectedSlug); } catch { /* Private mode may deny storage. */ }
    const controller = new AbortController();
    void loadHistory(selectedSlug, controller.signal);
    setRealtime('connecting');
    const source = api.events(selectedSlug, (station) => {
      setStations((current) => current.map((item) => item.slug === station.slug ? station : item));
      setReceivedAt(Date.now());
      void loadHistory(selectedSlug);
    }, setRealtime);

    return () => { controller.abort(); source.close(); };
  }, [loadHistory, selectedSlug]);

  useEffect(() => {
    const timer = window.setInterval(() => setClock(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    const onInstall = (event: Event) => { event.preventDefault(); setInstallPrompt(event as InstallPromptEvent); };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    window.addEventListener('beforeinstallprompt', onInstall);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('beforeinstallprompt', onInstall);
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio === null || selected === null) return;
    const resume = userStarted.current && !audio.paused;
    audio.src = selected.streamUrl;
    audio.load();
    reconnectAttempt.current = 0;
    setAudioState(resume ? 'loading' : 'idle');
    if (resume) void audio.play().catch(() => setAudioState('blocked'));
  }, [selected?.slug, selected?.streamUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (sleepDeadline !== null && clock >= sleepDeadline) {
      audio?.pause();
      userStarted.current = false;
      setSleepDeadline(null);
      setAudioState('paused');
    }
  }, [clock, sleepDeadline]);

  useEffect(() => {
    if (!('mediaSession' in navigator) || nowPlaying === null) return;
    const artwork = nowPlaying.current.coverUrl === null ? [] : [{ src: nowPlaying.current.coverUrl }];
    navigator.mediaSession.metadata = new MediaMetadata({ title: nowPlaying.current.title, artist: nowPlaying.current.artist, album: selected?.name ?? 'Radio', artwork });
    navigator.mediaSession.setActionHandler('play', () => { void audioRef.current?.play(); });
    navigator.mediaSession.setActionHandler('pause', () => audioRef.current?.pause());
    navigator.mediaSession.setActionHandler('stop', () => { audioRef.current?.pause(); userStarted.current = false; });
    return () => {
      navigator.mediaSession.setActionHandler('play', null);
      navigator.mediaSession.setActionHandler('pause', null);
      navigator.mediaSession.setActionHandler('stop', null);
    };
  }, [nowPlaying, selected?.name]);

  useEffect(() => () => {
    if (reconnectTimer.current !== null) window.clearTimeout(reconnectTimer.current);
  }, []);

  const togglePlayback = async (): Promise<void> => {
    const audio = audioRef.current;
    if (audio === null || selected === null) return;
    if (!audio.paused) {
      audio.pause();
      setAudioState('paused');
      return;
    }
    userStarted.current = true;
    setAudioState('loading');
    try { await audio.play(); } catch { setAudioState('blocked'); }
  };

  const reconnect = (): void => {
    const audio = audioRef.current;
    if (!userStarted.current || audio === null || !online || reconnectTimer.current !== null) return;
    setAudioState('reconnecting');
    const delay = reconnectDelayMs(reconnectAttempt.current++);
    reconnectTimer.current = window.setTimeout(() => {
      reconnectTimer.current = null;
      audio.load();
      void audio.play().catch(() => reconnect());
    }, delay);
  };

  const chooseStation = (slug: string): void => {
    const shouldResume = userStarted.current && audioRef.current !== null && !audioRef.current.paused;
    setSelectedSlug(slug);
    if (shouldResume) userStarted.current = true;
  };

  const startSleepTimer = (minutes: number): void => {
    const now = Date.now();
    setClock(now);
    setSleepDeadline(now + minutes * 60_000);
  };

  const install = async (): Promise<void> => {
    if (installPrompt === null) return;
    await installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  if (loading) return <main className="listener-empty"><span className="listener-loader" /><p>Налаштовуємо сигнал…</p></main>;
  if (stations.length === 0) return <main className="listener-empty"><p className="listener-kicker">SWEETLANA</p><h1>Ефір готується</h1><p>{error ?? 'Щойно адміністратор увімкне першу станцію, вона з’явиться тут автоматично.'}</p></main>;

  const isPlaying = audioState === 'playing';
  const statusText = !online ? 'офлайн' : realtime === 'live' ? 'дані наживо' : realtime === 'reconnecting' ? 'відновлюємо дані' : 'підключення';
  const artwork = nowPlaying?.current.coverUrl ?? selected?.coverUrl ?? null;

  return (
    <main className={`listener ${isPlaying ? 'is-playing' : ''}`}>
      <audio ref={audioRef} preload="none" onPlaying={() => { reconnectAttempt.current = 0; setAudioState('playing'); }} onPause={() => setAudioState(userStarted.current ? 'paused' : 'idle')} onWaiting={() => setAudioState('loading')} onStalled={reconnect} onError={reconnect} />
      <header className="listener-header">
        <a className="listener-brand" href="/" aria-label="SweetLana — на головну"><span>SL</span> SWEETLANA</a>
        <label className="listener-station-picker">Станція
          <select value={selectedSlug} onChange={(event) => chooseStation(event.target.value)} aria-label="Оберіть радіостанцію">
            {stations.map((station) => <option key={station.id} value={station.slug}>{station.name}</option>)}
          </select>
        </label>
        <div className="listener-live-state" aria-live="polite"><i className={online && selected?.status === 'on_air' ? 'on' : ''} />{statusText}</div>
      </header>

      <section className="listener-hero" aria-labelledby="current-track">
        <div className="listener-artwork" aria-hidden="true">
          {artwork === null ? <div className="listener-artwork-fallback"><span>R</span><i /><i /><i /></div> : <img src={artwork} alt="" />}
        </div>
        <div className="listener-copy">
          <p className="listener-kicker">{selected?.status === 'on_air' ? 'ЗАРАЗ В ЕФІРІ' : 'СТАНЦІЯ ГОТОВА'}</p>
          <h1 id="current-track">{nowPlaying?.current.title ?? selected?.name}</h1>
          <p className="listener-artist">{nowPlaying?.current.artist || 'Безперервний радіоефір'}</p>
          <div className="listener-progress" aria-label="Прогрес поточної композиції">
            <span style={{ width: `${progress?.percent ?? 0}%` }} />
          </div>
          <div className="listener-time"><span>{progress === null ? 'НАЖИВО' : formatDuration(progress.elapsedMs)}</span><span>{progress === null ? '—:—' : formatDuration(progress.durationMs)}</span></div>

          <div className="listener-controls">
            <button className="listener-play" type="button" onClick={() => void togglePlayback()} aria-label={isPlaying ? 'Зупинити ефір' : 'Слухати ефір'} disabled={!online}>
              <span aria-hidden="true">{isPlaying ? 'Ⅱ' : '▶'}</span>{isPlaying ? 'ПАУЗА' : audioState === 'reconnecting' ? 'ВІДНОВЛЕННЯ' : 'СЛУХАТИ'}</button>
            <label className="listener-volume"><span>Гучність</span><input type="range" min="0" max="1" step="0.05" value={volume} onChange={(event) => { const next = Number(event.target.value); setVolume(next); if (audioRef.current !== null) audioRef.current.volume = next; }} /></label>
          </div>
          {audioState === 'blocked' && <p className="listener-notice" role="status">Браузер зупинив автозапуск. Натисніть «Слухати» ще раз.</p>}
          {nowPlaying?.next !== null && nowPlaying?.next !== undefined && <p className="listener-next"><span>Далі</span>{nowPlaying.next.artist} — {nowPlaying.next.title}</p>}
        </div>
      </section>

      <section className="listener-history" aria-labelledby="history-title">
        <div className="listener-section-heading"><p className="listener-kicker">НЕЩОДАВНО</p><h2 id="history-title">Історія ефіру</h2></div>
        {history.length === 0 ? <p className="listener-history-empty">Історія з’явиться після перших композицій.</p> : <ol>{history.map((item) => <li key={item.eventId}><time dateTime={item.startedAt}>{new Intl.DateTimeFormat('uk-UA', { hour: '2-digit', minute: '2-digit' }).format(new Date(item.startedAt))}</time><div><strong>{item.title}</strong><span>{item.artist}</span></div><small>{itemTypeLabel(item.type)}</small></li>)}</ol>}
      </section>

      <footer className="listener-footer">
        <div><p className="listener-kicker">ТАЙМЕР СНУ</p><div className="sleep-buttons">{[15, 30, 60, 90].map((minutes) => <button key={minutes} type="button" onClick={() => startSleepTimer(minutes)}>{minutes} хв</button>)}{sleepDeadline !== null && <button type="button" className="active" onClick={() => setSleepDeadline(null)}>Скасувати · {sleepRemaining(sleepDeadline, clock)}</button>}</div></div>
        {installPrompt !== null && <button className="listener-install" type="button" onClick={() => void install()}>Встановити застосунок</button>}
        <p className="listener-footnote">Плеєр не залежить від метаданих аудіопотоку. Поточний ефір і час надходять безпосередньо із сервера.</p>
      </footer>
    </main>
  );
}
